import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import posthog from 'posthog-js'
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box } from '@mui/material'
import { config } from '../config/environment'

/**
 * PostHogProvider - Feature flags and product analytics via PostHog
 *
 * This provider handles:
 * - PostHog initialization with API key from environment config
 * - Feature flag checking via useFeatureFlag hook
 * - Event tracking via trackEvent function
 *
 * Config values (from environment.ts, generated at build time):
 * - ANALYTICS_API_KEY: Analytics API key (PostHog project key)
 * - ANALYTICS_HOST: Analytics host (defaults to https://us.i.posthog.com)
 *
 * NOTE: User identification should be done separately using identifyUser()
 * after user identity is available (e.g., after login/registration).
 * This keeps PostHogProvider independent of UserIdentityContext.
 */

interface PostHogContextType {
  isInitialized: boolean
  areFlagsLoaded: boolean
  isFeatureEnabled: (flag: string) => boolean
  trackEvent: (event: string, properties?: Record<string, any>) => void
  identifyUser: (email: string, properties?: { department?: string; userId?: string }) => void
}

const PostHogContext = createContext<PostHogContextType | undefined>(undefined)

interface PostHogProviderProps {
  children: ReactNode
  /**
   * Restrict browser-side flag evaluation to these keys. When supplied, the
   * PostHog SDK's automatic all-flags request is disabled and this provider
   * makes one scoped /flags request instead. Analytics capture remains active.
   */
  featureFlagKeys?: readonly string[]
}

const SCOPED_FLAGS_TIMEOUT_MS = 2_000

type PostHogFlagsResponse = {
  featureFlags?: Record<string, unknown>
  flags?: Record<string, unknown>
}

function readFlagEnabled(response: PostHogFlagsResponse, key: string): boolean {
  const detailed = response.flags?.[key]
  if (detailed && typeof detailed === 'object' && !Array.isArray(detailed)) {
    const value = detailed as {enabled?: unknown; variant?: unknown}
    if (typeof value.enabled === 'boolean') return value.enabled
    return value.variant === true || typeof value.variant === 'string'
  }
  const legacy = response.featureFlags?.[key]
  return legacy === true || typeof legacy === 'string'
}

async function loadScopedFeatureFlags(
  host: string,
  apiKey: string,
  distinctId: string,
  keys: readonly string[],
  signal: AbortSignal,
): Promise<Record<string, boolean>> {
  const disabled = Object.fromEntries(keys.map(key => [key, false]))
  if (keys.length === 0) return disabled

  const response = await fetch(`${host.replace(/\/+$/, '')}/flags/?v=2`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      token: apiKey,
      distinct_id: distinctId,
      flag_keys_to_evaluate: keys,
    }),
    signal,
  })
  if (!response.ok) throw new Error(`PostHog flags request failed (${response.status})`)
  const body = await response.json() as PostHogFlagsResponse
  return Object.fromEntries(keys.map(key => [key, readFlagEnabled(body, key)]))
}

// e2e escape hatch: the automated suite must not depend on an external flag service (a
// fresh test browser has no entitled PostHog identity, so flag-gated UI blanks out
// nondeterministically). Vite only inlines VITE_-prefixed vars at build/dev time, so this
// is set exclusively by the e2e harness env and is inert everywhere else.
const E2E_FORCE_FLAGS = typeof import.meta !== 'undefined'
  && (import.meta as { env?: Record<string, string> }).env?.VITE_E2E_FORCE_FLAGS === 'all'

export function PostHogProvider({ children, featureFlagKeys }: PostHogProviderProps): JSX.Element {
  const [isInitialized, setIsInitialized] = useState(false)
  const [areFlagsLoaded, setAreFlagsLoaded] = useState(false)
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({})
  // Dev-only: surface a missing ANALYTICS_API_KEY as a dismissible modal so it's
  // not silently swallowed (flag-gated UI just vanishes otherwise). Never shown
  // outside local development.
  const [showKeyMissingModal, setShowKeyMissingModal] = useState(false)

  // Initialize PostHog
  useEffect(() => {
    const apiKey = config.ANALYTICS_API_KEY
    const host = config.ANALYTICS_HOST || 'https://us.i.posthog.com'
    const scopedFlagKeys = featureFlagKeys ? [...new Set(featureFlagKeys)] : null
    const abortController = new AbortController()
    let cancelled = false
    let timeoutId: number | undefined

    if (!apiKey) {
      console.warn(
        '[PostHog] ANALYTICS_API_KEY is not set — feature flags are DISABLED ' +
        '(flag-gated UI will be hidden, with no other error).\n' +
        '  Local dev: this key is injected into src/config/environment.ts at deploy ' +
        'from SSM, but is intentionally blank in the committed file. To enable flags ' +
        'locally, set the PostHog project key — see reach3insight-app/.env.example ' +
        '(ANALYTICS_API_KEY). Ask the team for the value; do NOT commit it.',
      )
      // Dev-only modal so the missing key is impossible to miss. Not in deployed envs.
      if (config.ENVIRONMENT === 'development') setShowKeyMissingModal(true)
      setIsInitialized(true) // Mark as initialized even without key (graceful degradation)
      setAreFlagsLoaded(true) // No flags to load
      return
    }

    try {
      const options = {
        api_host: host,
        // Ros supplies a one-key allowlist. Prevent the browser SDK from
        // evaluating every project flag; authenticated Ros grants come from
        // the server-owned /api/r3/features snapshot instead.
        advanced_disable_feature_flags: scopedFlagKeys !== null,
        // Required: capture person profiles for all users (not just identified)
        // This ensures feature flags work before and after identify() is called
        person_profiles: 'always' as const,
        loaded: () => {
          console.log('[PostHog] Initialized successfully')
          setIsInitialized(true)
          if (scopedFlagKeys !== null) {
            timeoutId = window.setTimeout(() => abortController.abort(), SCOPED_FLAGS_TIMEOUT_MS)
            void loadScopedFeatureFlags(
              host,
              apiKey,
              posthog.get_distinct_id(),
              scopedFlagKeys,
              abortController.signal,
            ).then(flags => {
              if (cancelled) return
              setFeatureFlags(flags)
              setAreFlagsLoaded(true)
              console.log('[PostHog] Scoped feature flags loaded:', scopedFlagKeys)
            }).catch(error => {
              if (cancelled) return
              console.error('[PostHog] Scoped feature flags failed:', error)
              setFeatureFlags(Object.fromEntries(scopedFlagKeys.map(key => [key, false])))
              setAreFlagsLoaded(true)
            }).finally(() => {
              if (timeoutId !== undefined) window.clearTimeout(timeoutId)
            })
          }
        }
      }

      // posthog-js is a module singleton. React StrictMode runs this effect twice
      // in development: the second init is a no-op and does not invoke `loaded`.
      // Resume from the already-loaded singleton so auth cannot hang forever.
      if (posthog.__loaded) options.loaded()
      else posthog.init(apiKey, options)

      if (scopedFlagKeys === null) {
        // Studio and other existing consumers retain the normal all-flags SDK behavior.
        posthog.onFeatureFlags((flags) => {
          const flagState: Record<string, boolean> = {}
          flags.forEach((flag) => {
            flagState[flag] = posthog.isFeatureEnabled(flag) ?? false
          })
          setFeatureFlags(flagState)
          setAreFlagsLoaded(true)
          console.log('[PostHog] Feature flags loaded:', flags)
        })
      }
    } catch (error) {
      console.error('[PostHog] Initialization failed:', error)
      setIsInitialized(true) // Still mark as initialized for graceful degradation
      setAreFlagsLoaded(true) // Assume no flags on error
    }

    return () => {
      cancelled = true
      abortController.abort()
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
  }, [])

  const isFeatureEnabled = useCallback((flag: string): boolean => {
    if (E2E_FORCE_FLAGS) {
      if (typeof window !== 'undefined' && window.localStorage.getItem(`e2e_ff_${flag}`) === 'false') return false
      return true
    }

    // If analytics not configured, default to false (feature disabled)
    if (!config.ANALYTICS_API_KEY) {
      return false
    }

    // A scoped consumer may only read its allowlisted response. Never fall
    // through to values the SDK persisted from an earlier all-flags session.
    if (featureFlagKeys) {
      return featureFlagKeys.includes(flag) && featureFlags[flag] === true
    }

    // Check cached flags first, then PostHog directly for legacy consumers.
    if (flag in featureFlags) {
      return featureFlags[flag]
    }

    return posthog.isFeatureEnabled(flag) ?? false
  }, [featureFlags, featureFlagKeys])

  const trackEvent = useCallback((event: string, properties?: Record<string, any>): void => {
    if (!config.ANALYTICS_API_KEY) {
      return // Silently skip if not configured
    }

    posthog.capture(event, properties)
  }, [])

  /**
   * Identify user for feature flag targeting
   * Call this after user logs in or registers
   *
   * @param email - User's email (used as distinct_id in PostHog)
   * @param properties - Optional user properties for targeting (department, userId)
   */
  const identifyUser = useCallback((email: string, properties?: { department?: string; userId?: string }): void => {
    if (!config.ANALYTICS_API_KEY) {
      return // Silently skip if not configured
    }

    posthog.identify(email, {
      email: email,
      department: properties?.department,
      userId: properties?.userId
    })
    // Scoped Ros flags are bootstrap-only and intentionally remain anonymous.
    // Other consumers retain their existing user-targeted reload behavior.
    if (!featureFlagKeys) posthog.reloadFeatureFlags()
    console.log('[PostHog] User identified:', email)
  }, [featureFlagKeys])

  const value: PostHogContextType = {
    isInitialized,
    areFlagsLoaded: E2E_FORCE_FLAGS ? true : areFlagsLoaded,
    isFeatureEnabled,
    trackEvent,
    identifyUser
  }

  return (
    <PostHogContext.Provider value={value}>
      {children}
      {showKeyMissingModal && <PostHogKeyMissingDialog
        open
        onClose={() => setShowKeyMissingModal(false)}
      />}
    </PostHogContext.Provider>
  )
}

/**
 * Dev-only modal shown when ANALYTICS_API_KEY is missing. The key is injected
 * into environment.ts from SSM at deploy, but is intentionally blank in the
 * committed file — so locally, flag-gated UI just vanishes with no error. This
 * makes that state impossible to miss. Never rendered in deployed environments
 * (gated on config.ENVIRONMENT === 'development' at the call site).
 */
function PostHogKeyMissingDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const codeSx = { px: 0.5, py: 0.25, bgcolor: 'action.hover', borderRadius: 0.5, fontFamily: 'monospace' }
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Feature flags are disabled (local dev)</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <p style={{ marginTop: 0 }}>
            <strong>ANALYTICS_API_KEY</strong> is not set, so PostHog feature flags
            are off. Any flag-gated UI will be hidden — with no other error. If
            something you expect to see is missing, this is why.
          </p>
          <p style={{ marginBottom: 0 }}>
            To enable flags locally, set the PostHog project key in{' '}
            <Box component="code" sx={codeSx}>packages/shared/src/config/environment.ts</Box>{' '}
            (<strong>ANALYTICS_API_KEY</strong>) for your local run only — see{' '}
            <Box component="code" sx={codeSx}>.env.example</Box>. Ask the team for
            the value, and <strong>do not commit it</strong>.
          </p>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Got it</Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Hook to access PostHog context
 */
export function usePostHog(): PostHogContextType {
  const context = useContext(PostHogContext)

  if (context === undefined) {
    throw new Error('usePostHog must be used within a PostHogProvider')
  }

  return context
}

/**
 * Convenience hook for checking a single feature flag
 *
 * @param flag - Feature flag name (e.g., 'visual-semiotics-enabled')
 * @returns boolean indicating if feature is enabled
 *
 * @example
 * const isVisualSemioticsEnabled = useFeatureFlag('visual-semiotics-enabled')
 */
export function useFeatureFlag(flag: string): boolean {
  const { isFeatureEnabled } = usePostHog()
  return isFeatureEnabled(flag)
}

export { PostHogContext }
